import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Wrench, Loader2, RotateCcw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const MaintenanceSection = ({ onSuccess, onError }) => {
  const [running, setRunning] = useState(false);
  const [report, setReport] = useState(null);

  // Rebuild-from-Telegram local state
  const [rebuildOpen, setRebuildOpen] = useState(false);
  const [rebuildNumeri, setRebuildNumeri] = useState('2498, 2500, 2501, 2504, 2506, 2508, 2511');
  const [rebuildOverrides, setRebuildOverrides] = useState('');
  const [rebuildRunning, setRebuildRunning] = useState(false);
  const [rebuildReport, setRebuildReport] = useState(null);

  const handleCleanup = async () => {
    if (!window.confirm(
      'Avvio pulizia duplicati su tutti gli interventi.\n\n' +
      '• Verranno deduplicati audio_files, foto e persone_identificate.\n' +
      '• Un backup completo dei documenti modificati sarà salvato nella collection `intervention_backups` PRIMA della scrittura.\n\n' +
      'Procedere?'
    )) return;
    setRunning(true);
    setReport(null);
    try {
      const res = await axios.post(`${API_URL}/api/admin/cleanup-duplicates`, {}, { withCredentials: true });
      setReport(res.data);
      onSuccess(res.data.message);
    } catch (err) {
      onError('Errore pulizia: ' + (err.response?.data?.detail || err.message));
    } finally {
      setRunning(false);
    }
  };

  const parseNumeri = (raw) =>
    raw.split(/[\s,;\n]+/).map(s => s.trim()).filter(Boolean);

  const parseOverrides = (raw) => {
    // Accept lines like: "12345 -> 2508" or "12345 → 2508" or "12345=2508" or "12345,2508"
    const out = [];
    raw.split(/\n+/).forEach((line) => {
      const m = line.match(/(\d+)\s*(?:->|→|=|,|\s)\s*(\d+)/);
      if (m) {
        out.push({ msg_id: parseInt(m[1], 10), target_numero: m[2] });
      }
    });
    return out;
  };

  const handleRebuild = async () => {
    const numeri = parseNumeri(rebuildNumeri);
    const overrides = parseOverrides(rebuildOverrides);
    if (numeri.length === 0) {
      onError('Inserisci almeno un numero di intervento');
      return;
    }
    const ovText = overrides.length
      ? `\n\nOverride (${overrides.length}):\n` + overrides.map(o => `  • msg ${o.msg_id} → intervento ${o.target_numero}`).join('\n')
      : '';
    if (!window.confirm(
      `RICOSTRUZIONE DA TELEGRAM (DISTRUTTIVA)\n\n` +
      `Interventi (${numeri.length}): ${numeri.join(', ')}${ovText}\n\n` +
      '⚠️ Per ogni intervento verranno:\n' +
      '  1. Salvato un backup completo in intervention_backups\n' +
      '  2. Cancellati audio_files, foto, persone_identificate\n' +
      '  3. Azzerate le 8 sezioni AI del modulo\n' +
      '  4. Riscaricati i media da Telegram nel range message_id corretto\n' +
      '  5. Ri-trascritti i vocali e ri-fatto OCR documenti\n\n' +
      'L\'operazione gira in background — puoi chiudere questa pagina, il lavoro continua.\n\n' +
      'Procedere?'
    )) return;
    setRebuildRunning(true);
    setRebuildReport(null);
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/rebuild-from-telegram`,
        { numeri, overrides },
        { withCredentials: true }
      );
      const batchId = res.data.batch_id;
      onSuccess(`Ricostruzione avviata (batch ${batchId.slice(-8)}). Aggiornamento ogni 3s...`);
      setRebuildReport({ ...res.data, status: 'queued' });
      // Start polling
      pollRebuildStatus(batchId);
    } catch (err) {
      onError('Errore avvio ricostruzione: ' + (err.response?.data?.detail || err.message));
      setRebuildRunning(false);
    }
  };

  const handleCancelRebuild = async () => {
    if (!rebuildReport?.batch_id) return;
    if (!window.confirm('Cancellare il job in corso? I media già processati rimarranno, il loop si fermerà entro qualche secondo.')) return;
    try {
      await axios.post(
        `${API_URL}/api/admin/rebuild-cancel/${rebuildReport.batch_id}`,
        {},
        { withCredentials: true }
      );
      onSuccess('Cancellazione richiesta — attendi qualche secondo');
    } catch (err) {
      onError('Errore cancellazione: ' + (err.response?.data?.detail || err.message));
    }
  };

  const pollRebuildStatus = (batchId) => {
    let attempts = 0;
    const maxAttempts = 1200; // 1200 * 3s = 1 hour max
    const poll = async () => {
      attempts += 1;
      if (attempts > maxAttempts) {
        setRebuildRunning(false);
        onError('Polling interrotto dopo 1 ora — controlla i log del backend');
        return;
      }
      try {
        const res = await axios.get(
          `${API_URL}/api/admin/rebuild-status/${batchId}`,
          { withCredentials: true }
        );
        setRebuildReport(res.data);
        if (res.data.status === 'completed' || res.data.status === 'cancelled') {
          setRebuildRunning(false);
          if (res.data.status === 'cancelled') {
            onError(`Ricostruzione cancellata (${batchId.slice(-8)})`);
          } else {
            onSuccess(`Ricostruzione completata (${batchId.slice(-8)})`);
          }
          return;
        }
        if (res.data.status === 'failed') {
          setRebuildRunning(false);
          onError(`Ricostruzione fallita: ${res.data.fatal_error || 'errore sconosciuto'}`);
          return;
        }
        setTimeout(poll, 3000);
      } catch (err) {
        setRebuildRunning(false);
        onError('Errore polling: ' + (err.response?.data?.detail || err.message));
      }
    };
    setTimeout(poll, 1500);
  };

  return (
    <div className="bg-[#121212] border border-[#27272A] p-6 mt-6" data-testid="maintenance-section">
      <h3
        className="text-lg font-semibold uppercase mb-5 flex items-center gap-2"
        style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
      >
        <Wrench className="w-5 h-5 text-amber-400" />
        Manutenzione database
      </h3>

      <p className="text-sm text-zinc-400 mb-4 leading-relaxed">
        Esegui la pulizia se sospetti che alcuni interventi abbiano file
        duplicati (vocali, foto o persone) salvati per errore. Il sistema fa
        un backup automatico prima di modificare qualsiasi cosa.
      </p>

      <div className="flex flex-wrap gap-3">
        <Button
          onClick={handleCleanup}
          disabled={running}
          className="bg-amber-500 hover:bg-amber-600 text-black rounded-none uppercase tracking-wide font-medium"
          data-testid="cleanup-duplicates-btn"
        >
          {running
            ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Pulizia in corso...</>
            : <><Wrench className="w-4 h-4 mr-2" /> Pulisci duplicati</>}
        </Button>

        <Button
          onClick={() => setRebuildOpen(o => !o)}
          variant="outline"
          className="border-[#E3000F] bg-transparent text-[#E3000F] hover:bg-[#E3000F]/10 rounded-none uppercase tracking-wide"
          data-testid="rebuild-toggle-btn"
        >
          <RotateCcw className="w-4 h-4 mr-2" />
          Ricostruisci da Telegram
        </Button>
      </div>

      {report && (
        <div className="mt-5 border border-[#27272A] bg-[#0a0a0a] p-4 text-sm" data-testid="cleanup-report">
          <p className="text-zinc-300 mb-2">
            <strong>Riepilogo:</strong> {report.scanned} interventi scansionati,{' '}
            <span className="text-amber-300">{report.dirty}</span> con duplicati corretti.
          </p>
          <p className="text-zinc-400 text-xs">
            Audio rimossi: <span className="text-white">{report.removed_audio}</span>{' · '}
            Foto: <span className="text-white">{report.removed_foto}</span>{' · '}
            Persone: <span className="text-white">{report.removed_persone}</span>
          </p>
          {report.batch_id && (
            <p className="text-zinc-500 text-[11px] font-mono mt-2">batch_id: {report.batch_id}</p>
          )}
          {report.details && report.details.length > 0 && (
            <div className="mt-3 max-h-48 overflow-y-auto border-t border-[#27272A] pt-2">
              {report.details.map((d, i) => (
                <div key={i} className="text-xs text-zinc-400 py-1 font-mono">
                  #{d.numero_intervento}: audio {d.before.audio_files}→{d.after.audio_files},
                  foto {d.before.foto}→{d.after.foto},
                  persone {d.before.persone_identificate}→{d.after.persone_identificate}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {rebuildOpen && (
        <div className="mt-5 border border-[#E3000F]/30 bg-[#0a0a0a] p-4 space-y-3" data-testid="rebuild-panel">
          <div className="border-l-2 border-[#E3000F] pl-3">
            <p className="text-zinc-300 text-sm font-medium mb-1">Ricostruzione da Telegram</p>
            <p className="text-zinc-500 text-xs leading-relaxed">
              Operazione DISTRUTTIVA: cancella media + sezioni AI degli interventi
              indicati e li riscarica/ricompila da zero usando i message_id Telegram
              corretti. Backup automatico in <code className="text-zinc-400">intervention_backups</code>.
            </p>
          </div>

          <div>
            <label className="text-zinc-400 text-xs uppercase tracking-wider block mb-1">
              Numeri intervento (separati da virgola, spazio o a capo)
            </label>
            <textarea
              value={rebuildNumeri}
              onChange={(e) => setRebuildNumeri(e.target.value)}
              rows={2}
              className="w-full bg-[#1A1A1A] border border-[#27272A] focus:border-[#E3000F] outline-none text-zinc-200 text-sm font-mono px-3 py-2"
              data-testid="rebuild-numeri-input"
              placeholder="2498, 2500, 2501, 2504, 2506, 2508, 2511"
            />
          </div>

          <div>
            <label className="text-zinc-400 text-xs uppercase tracking-wider block mb-1">
              Override (opzionale, una per riga)
            </label>
            <textarea
              value={rebuildOverrides}
              onChange={(e) => setRebuildOverrides(e.target.value)}
              rows={3}
              className="w-full bg-[#1A1A1A] border border-[#27272A] focus:border-[#E3000F] outline-none text-zinc-200 text-sm font-mono px-3 py-2"
              data-testid="rebuild-overrides-input"
              placeholder={'12345 → 2508\nesempio: il messaggio Telegram con id 12345 deve finire nell\'intervento 2508'}
            />
            <p className="text-zinc-500 text-[11px] mt-1 leading-relaxed">
              Per il caso "vocale 1:28 di 2511 → 2508": apri Telegram, click destro sul vocale → "Copia link",
              prendi l'ultimo numero dell'URL (es. <code>https://t.me/c/.../<strong>12345</strong></code>)
              e scrivi qui: <code>12345 → 2508</code>
            </p>
          </div>

          <div className="flex gap-2">
          <Button
            onClick={handleRebuild}
            disabled={rebuildRunning}
            className="bg-[#E3000F] hover:bg-[#CC000D] text-white rounded-none uppercase tracking-wide flex-1"
            data-testid="rebuild-execute-btn"
          >
            {rebuildRunning
              ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Ricostruzione in corso...</>
              : <><RotateCcw className="w-4 h-4 mr-2" /> Esegui ricostruzione</>}
          </Button>

          {rebuildRunning && rebuildReport?.batch_id && (
            <Button
              onClick={handleCancelRebuild}
              variant="outline"
              className="border-zinc-500 bg-transparent text-zinc-300 hover:bg-zinc-800 rounded-none uppercase tracking-wide"
              data-testid="rebuild-cancel-btn"
            >
              Annulla
            </Button>
          )}
          </div>

          {rebuildReport && (
            <div className="mt-3 border border-[#27272A] bg-[#050505] p-3 text-xs space-y-2" data-testid="rebuild-report">
              <div className="flex items-center justify-between">
                <span
                  className={
                    rebuildReport.status === 'completed' ? 'text-green-400 font-medium' :
                    rebuildReport.status === 'failed' ? 'text-red-400 font-medium' :
                    rebuildReport.status === 'running' ? 'text-amber-300 font-medium' :
                    'text-zinc-400'
                  }
                >
                  Stato: {rebuildReport.status?.toUpperCase() || 'QUEUED'}
                  {rebuildReport.status === 'running' && rebuildReport.current_intervention && (
                    <span className="text-zinc-500 ml-2">
                      → #{rebuildReport.current_intervention} (msg {rebuildReport.current_msg || '...'})
                    </span>
                  )}
                </span>
                <span className="text-zinc-600 text-[10px] font-mono">
                  {rebuildReport.batch_id?.slice(-13)}
                </span>
              </div>
              <p className="text-zinc-400">
                Vocali: <span className="text-white">{rebuildReport.processed?.voice || 0}</span>{' · '}
                Audio: <span className="text-white">{rebuildReport.processed?.audio || 0}</span>{' · '}
                Foto: <span className="text-white">{rebuildReport.processed?.photo || 0}</span>{' · '}
                Documenti: <span className="text-white">{rebuildReport.processed?.document || 0}</span>{' · '}
                Override: <span className="text-amber-300">{rebuildReport.overridden || 0}</span>{' · '}
                Saltati: <span className="text-zinc-500">{rebuildReport.skipped || 0}</span>
              </p>
              {rebuildReport.fatal_error && (
                <p className="text-red-400 text-[11px] mt-1">⚠ {rebuildReport.fatal_error}</p>
              )}
              {rebuildReport.errors?.length > 0 && (
                <details>
                  <summary className="text-amber-400 cursor-pointer">
                    {rebuildReport.errors.length} errori (non bloccanti)
                  </summary>
                  <pre className="text-[10px] text-zinc-500 mt-1 max-h-32 overflow-y-auto">
                    {rebuildReport.errors.slice(-30).join('\n')}
                  </pre>
                </details>
              )}
              {rebuildReport.ranges?.length > 0 && (
                <details>
                  <summary className="text-zinc-400 cursor-pointer">
                    Range processati ({rebuildReport.ranges.length})
                  </summary>
                  <pre className="text-[10px] text-zinc-500 mt-1 max-h-32 overflow-y-auto font-mono">
                    {rebuildReport.ranges.map(r => `#${r.numero}: msg ${r.from_msg_id} → ${r.to_msg_id}`).join('\n')}
                  </pre>
                </details>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
